// Shared helpers for game engines. Pure functions only.

import type { Round } from '../types';
import type { Cents, GameConfig, LedgerEntry, RoundCtx } from './types';
import { netScore } from './handicap';

/** Effective score for a hole under the game's scoring mode; null if not entered. */
export function scoreFor(
  ctx: RoundCtx,
  config: GameConfig,
  playerId: string,
  holeIdx: number
): number | null {
  if (config.scoring === 'gross') {
    return ctx.round.holes[holeIdx]?.scores[playerId]?.gross ?? null;
  }
  return netScore(ctx, playerId, holeIdx);
}

/** True when every participant has a gross score on the hole. */
export function holeComplete(round: Round, participantIds: string[], holeIdx: number): boolean {
  const hole = round.holes[holeIdx];
  if (!hole) return false;
  return participantIds.every((id) => hole.scores[id]?.gross != null);
}

/** Highest hole index (0-based, exclusive) such that ALL holes before it are complete. */
export function completeThrough(round: Round, participantIds: string[]): number {
  let n = 0;
  while (n < round.holes.length && holeComplete(round, participantIds, n)) n++;
  return n;
}

/**
 * Indexes of every complete hole, wherever it sits on the card.
 *
 * `completeThrough` reads a LEADING RUN, which is right for the order-dependent games
 * (Skins carries onto the next hole, Nassau closes a front nine) but wrong for a shotgun
 * start: a group that teed off on 5 has nothing complete at the front of the array and
 * would read as having played nothing all afternoon. Games whose scoring is order-free —
 * a scramble total, stroke play — should count holes with this instead.
 */
export function completeHoleIndexes(round: Round, participantIds: string[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < round.holes.length; i++) {
    if (holeComplete(round, participantIds, i)) out.push(i);
  }
  return out;
}

/**
 * Winner(s) of a hole: participants with the lowest effective score.
 * Returns [] if any participant hasn't scored (hole not comparable).
 */
export function holeWinners(
  ctx: RoundCtx,
  config: GameConfig,
  participantIds: string[],
  holeIdx: number
): string[] {
  const scores = participantIds.map((id) => ({ id, s: scoreFor(ctx, config, id, holeIdx) }));
  if (scores.some((x) => x.s === null)) return [];
  const min = Math.min(...scores.map((x) => x.s as number));
  return scores.filter((x) => x.s === min).map((x) => x.id);
}

/** Best (lowest) effective score among a team on a hole; null if nobody on the team has scored. */
export function teamBest(
  ctx: RoundCtx,
  config: GameConfig,
  team: string[],
  holeIdx: number
): number | null {
  const vals = team
    .map((id) => scoreFor(ctx, config, id, holeIdx))
    .filter((v): v is number => v !== null);
  return vals.length ? Math.min(...vals) : null;
}

/** Worst (highest) effective score among a team on a hole; null unless ALL have scored. */
export function teamWorst(
  ctx: RoundCtx,
  config: GameConfig,
  team: string[],
  holeIdx: number
): number | null {
  const vals = team.map((id) => scoreFor(ctx, config, id, holeIdx));
  if (vals.some((v) => v === null)) return null;
  return Math.max(...(vals as number[]));
}

/** Par for a player's hole (their own tee). */
export function parFor(ctx: RoundCtx, playerId: string, holeIdx: number): number {
  const rp = ctx.round.players.find((p) => p.playerId === playerId);
  const tee = ctx.course.tees.find((t) => t.id === rp?.teeId) ?? ctx.course.tees[0];
  return tee.holes[holeIdx]?.par ?? 4;
}

/** Build a zeroed ledger for participants, then apply deltas; guarantees every participant appears. */
export function makeLedger(participantIds: string[]): Map<string, Cents> {
  return new Map(participantIds.map((id) => [id, 0]));
}

export function ledgerToEntries(ledger: Map<string, Cents>): LedgerEntry[] {
  return [...ledger.entries()].map(([playerId, amount]) => ({ playerId, amount }));
}

/** Split `total` cents evenly among ids, distributing the remainder cent-by-cent deterministically. */
export function splitEven(total: Cents, ids: string[]): Map<string, Cents> {
  const out = new Map<string, Cents>();
  if (!ids.length) return out;
  const base = Math.trunc(total / ids.length);
  let rem = total - base * ids.length;
  for (const id of ids) {
    out.set(id, base + (rem > 0 ? 1 : rem < 0 ? -1 : 0));
    if (rem > 0) rem--;
    else if (rem < 0) rem++;
  }
  return out;
}

/**
 * Settle a hole/point between two sides of ANY size (2v2, 2v1, 3v2, 1v1, …), in cents.
 *
 * `side0Signed` is the amount side0 would move under the classic equal-size convention —
 * positive when side0 won, negative when side0 lost, zero on a tie (callers skip ties
 * before calling this). The LARGER side always moves by that same per-player amount (sign
 * flipped when side1 is the larger one), and the SMALLER side absorbs the matching total,
 * split evenly across its members with `splitEven` so the ledger still sums to zero.
 *
 * This is the "per head" rule for uneven teams: in a 2v1 at $5/hole each of the two plays
 * the solo for $5 (their own per-player amount, unchanged), so the solo — the smaller side,
 * always exactly one side here — wins or loses the full $10 that balances it.
 *
 * Equal-size sides reduce to exactly the old per-player convention: dividing the bigger
 * side's total by an equal-size smaller side returns that same per-player amount, so every
 * settled 2v2 round keeps computing byte-for-byte the same.
 */
export function settleSides(
  ledger: Map<string, Cents>,
  side0: string[],
  side1: string[],
  side0Signed: Cents
): void {
  if (side0Signed === 0 || side0.length === 0 || side1.length === 0) return;
  const bigIsSide0 = side0.length >= side1.length;
  const big = bigIsSide0 ? side0 : side1;
  const small = bigIsSide0 ? side1 : side0;
  const bigPerMember = bigIsSide0 ? side0Signed : -side0Signed;
  for (const id of big) ledger.set(id, (ledger.get(id) ?? 0) + bigPerMember);
  const bigTotal = bigPerMember * big.length;
  const smallSplits = splitEven(-bigTotal, small);
  for (const id of small) ledger.set(id, (ledger.get(id) ?? 0) + (smallSplits.get(id) ?? 0));
}

/**
 * A sensible default two-sided split for a game being configured for the first time: an
 * even halve, bigger side first. 4 players -> 2v2 (unchanged from before uneven sides
 * existed); 3 -> 2v1; 2 -> 1v1; 5 -> 3v2.
 */
export function defaultTeamSplit(ids: string[]): [string[], string[]] {
  const half = Math.ceil(ids.length / 2);
  return [ids.slice(0, half), ids.slice(half)];
}

/**
 * Validates a two-sided team split for a game that allows uneven sides (2v1, 3v2, 1v1, …):
 * each side needs at least one player, the sides must be disjoint, and together they must
 * be exactly the game's participants — no size constraint beyond that. `label` names the
 * game in the error text, e.g. "Best Ball".
 */
export function validateTeamSplit(
  label: string,
  teams: [string[], string[]] | null | undefined,
  participantIds: string[]
): string[] {
  if (!teams || teams[0].length === 0 || teams[1].length === 0) {
    return [`${label} needs at least one player on each side`];
  }
  const [team0, team1] = teams;
  const errs: string[] = [];
  if (team0.some((id) => team1.includes(id))) {
    errs.push('A player cannot be on both teams');
  }
  const union = [...new Set([...team0, ...team1])];
  const participants = [...new Set(participantIds)];
  if (union.length !== participants.length || !union.every((id) => participants.includes(id))) {
    errs.push(`${label} teams must include exactly the participants`);
  }
  return errs;
}

export const fmtToPar = (n: number): string => (n === 0 ? 'E' : n > 0 ? `+${n}` : `${n}`);

/**
 * A player's putts across a round, and how many holes actually carry a count.
 *
 * `holes` is the half that matters. Putts are recorded hole by hole and very often only on
 * some of them, so a bare total of 14 means nothing without knowing whether that was over
 * four holes or eighteen — and a total shown beside a finished round implies a
 * completeness the data does not have. Callers show both, or show nothing.
 *
 * A hole with no count is skipped rather than counted as zero: nobody holes out from the
 * tee, so a zero here always means "not recorded" and never means "no putts".
 */
export function puttsTotal(round: Round, playerId: string): { putts: number; holes: number } {
  let putts = 0;
  let holes = 0;
  for (const hole of round.holes) {
    const p = hole?.scores[playerId]?.putts;
    if (p == null || !Number.isFinite(p)) continue;
    putts += p;
    holes += 1;
  }
  return { putts, holes };
}

/** Whether anybody recorded a putt anywhere in this round — gates the putts column. */
export function roundHasPutts(round: Round): boolean {
  return round.holes.some((hole) =>
    Object.values(hole?.scores ?? {}).some((s) => s?.putts != null)
  );
}

/** One rotating-partner segment: a hole range plus the seat indexes making up each side. */
export type SeatSegment = {
  /** 0-based, inclusive. */
  start: number;
  /** 0-based, exclusive. */
  end: number;
  teamA: [number, number];
  teamB: [number, number];
};

/** The three partner pairings a foursome rotates through, in order. */
const SEAT_PAIRS: Array<{ teamA: [number, number]; teamB: [number, number] }> = [
  { teamA: [0, 1], teamB: [2, 3] },
  { teamA: [0, 2], teamB: [1, 3] },
  { teamA: [0, 3], teamB: [1, 2] },
];

/**
 * Split a round into the three rotating-partner segments used by Sixes and Scotch.
 *
 * 18 holes gives the familiar 1–6 / 7–12 / 13–18. Any other length divides the same way
 * (`ceil(n/3)` holes per segment, last segment ending on the final hole) so a nine-hole
 * round rotates at 3 and 6 instead of silently playing one pairing and never settling
 * the other two. Segments that would be empty on a very short round come back start===end
 * and are skipped by callers.
 */
export function rotatingSeatSegments(holeCount: number): SeatSegment[] {
  const n = Math.max(0, holeCount);
  const step = Math.max(1, Math.ceil(n / 3));
  return SEAT_PAIRS.map((pair, i) => ({
    start: Math.min(i * step, n),
    end: i === SEAT_PAIRS.length - 1 ? n : Math.min((i + 1) * step, n),
    ...pair,
  }));
}
